// Global Instructions Rule Applied!

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Empty, Spin, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { ChangeLogContextTable } from '../services/changeLogService.ts'
import { ChangeLogEvent } from '../../../state_manager/data_models/change_log_model.ts'
import { useChangeLog } from '../hooks/useChangeLog.ts'

const { Text } = Typography

// Nears-the-bottom threshold for the infinite scroll (same as the
// interactions feed).
const SCROLL_LOAD_THRESHOLD_PX = 250

// Field | Old Value | New Value grid, sized by the TAB CONTAINER (not the
// viewport — the sidebar can eat a third of a narrow window):
//   * capped at 960px so a wide screen doesn't spread the three cells apart
//     just to fill it — they must stay readable in one glance;
//   * below 560px of container width the grid stacks to a single column
//     (viewport media queries can't see the container, hence the
//     ResizeObserver in the tab).
const CHANGE_GRID_STACK_BELOW_PX = 560
const CHANGE_GRID_WIDE_CLASSES =
  'mt-1 grid w-full max-w-[960px] gap-x-4 gap-y-0.5 ' +
  'grid-cols-[minmax(140px,200px)_minmax(0,1fr)_minmax(0,1fr)]'
const CHANGE_GRID_STACKED_CLASSES = 'mt-1 grid w-full grid-cols-1 gap-y-0.5'

interface ChangeLogTabProps {
  contextTable: ChangeLogContextTable
  recordId?: string | null
}

const eventColor = (event: ChangeLogEvent): string => {
  if (event.event_title.endsWith('removed') || event.event_title.endsWith('deleted')) return '#ff4d4f'
  if (event.event_title.endsWith('added') || event.event_title.endsWith('created') || event.event_title.endsWith('restored')) return '#52c41a'
  return '#1677ff'
}

const ValueCell: React.FC<{ value: string | null; old?: boolean }> = ({ value, old = false }) =>
  value == null ? (
    <Text type='secondary' className='block'>
      —
    </Text>
  ) : old ? (
    <Text type='secondary' delete className='break-words block'>
      {value}
    </Text>
  ) : (
    <Text className='break-words block'>{value}</Text>
  )

const EventBlock: React.FC<{ event: ChangeLogEvent; stacked: boolean }> = ({ event, stacked }) => (
  <div
    className='w-full py-2 pl-3'
    style={{ borderLeft: `3px solid ${eventColor(event)}` }}
  >
    <div className='flex items-baseline gap-2 flex-wrap'>
      <Text strong>{event.event_title}</Text>
      {event.is_agent ? <Tag color='purple'>via agent</Tag> : null}
      <Text type='secondary' className='text-xs'>
        {event.user_name} · {dayjs(event.date_created).format('MMM D, YYYY h:mm A')}
      </Text>
    </div>
    {event.changes.length > 0 ? (
      <div className={stacked ? CHANGE_GRID_STACKED_CLASSES : CHANGE_GRID_WIDE_CLASSES}>
        {/* Header row only makes sense with three columns. */}
        {!stacked ? (
          <>
            <Text type='secondary' className='text-xs'>
              Field
            </Text>
            <Text type='secondary' className='text-xs'>
              Old Value
            </Text>
            <Text type='secondary' className='text-xs'>
              New Value
            </Text>
          </>
        ) : null}
        {event.changes.map((change, idx) => (
          // display:contents hands the three cells straight to the grid;
          // when stacked, the wrapper groups them as one separated block.
          <div key={`${event.id}-${idx}`} className={stacked ? 'mb-1.5' : 'contents'}>
            <Text type='secondary' className='break-words block'>
              {change.field}
            </Text>
            <ValueCell value={change.old} old />
            <ValueCell value={change.new} />
          </div>
        ))}
      </div>
    ) : null}
  </div>
)

/**
 * ChangeLogTab — shared, entity-generic change-history feed for full-forms
 * (mounted on the Account and Deal forms; other forms can mount it with one
 * line). Newest-first infinite-scroll feed of audit.logs events for the
 * record, including rolled-up child-row changes (contact links, roles,
 * topics, commitments, pipeline moves). Each event renders full-width with
 * a Field | Old Value | New Value grid. All rendering intelligence lives in
 * the get_change_log_data RPC — this component only lays the events out.
 */
export const ChangeLogTab: React.FC<ChangeLogTabProps> = ({ contextTable, recordId }) => {
  const { events, count, hasMore, loading, loadingMore, error, loadMore, refetch } = useChangeLog(
    contextTable,
    recordId
  )
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [stacked, setStacked] = useState(false)

  // Container-width watcher: stack the change grids to a single column when
  // the TAB gets too narrow for three readable columns. Container-based on
  // purpose — the sidebar can shrink the tab while the viewport stays wide.
  useEffect(() => {
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? 0
      // Ignore 0-width reads from hidden tab panes (display:none).
      if (width > 0) setStacked(width < CHANGE_GRID_STACK_BELOW_PX)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Infinite scroll: append the next page when the user nears the bottom
  // (same pattern as InteractionsSection).
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_LOAD_THRESHOLD_PX) {
        loadMore()
      }
    },
    [loadMore]
  )

  // Short first pages never fire a scroll event — keep fetching until the
  // feed fills the container (or runs out of pages).
  useEffect(() => {
    const el = containerRef.current
    if (!el || loading || loadingMore || !hasMore) return
    // clientHeight 0 = hidden tab pane (display:none) — don't fetch in the
    // background, the scroll handler takes over once the tab is visible.
    if (el.clientHeight > 0 && el.scrollHeight <= el.clientHeight) {
      loadMore()
    }
  }, [events.length, hasMore, loading, loadingMore, loadMore])

  if (loading && events.length === 0 && !error) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Spin size='large' />
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className='change-log flex flex-col h-full p-3 gap-2 overflow-y-auto'
    >
      {error ? (
        <Alert
          type='error'
          showIcon
          message='Could not load the change log'
          description={error}
          action={
            <Button size='small' onClick={refetch}>
              Retry
            </Button>
          }
        />
      ) : null}

      {events.length === 0 && !error ? (
        <Empty description='No changes recorded yet for this record.' className='my-10' />
      ) : (
        <>
          <Text type='secondary' className='text-xs'>
            {count} change {count === 1 ? 'event' : 'events'}
          </Text>
          {events.map(event => (
            <EventBlock key={event.id} event={event} stacked={stacked} />
          ))}
          {loadingMore ? (
            <div className='flex justify-center py-2'>
              <Spin size='small' />
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}

export default ChangeLogTab
